// ---------------------------------------------------------------------------
// FEMA IPAWS — the message STORE shapes (card FE-04, blocked by BE-11).
// `fema.types.ts` carries the wire CAP payload the operator authors and the
// per-submit outcome; THIS file is the row/thread shape the message list and
// thread view read back from `GET /v2/fema`.
//
// PROVISIONAL: BE-11 (the message store) had not landed when this was written
// (2026-09-03). Field names here are the best-guess contract from the FE-04
// card text and the existing `Ipaws_Message` references in fema.types.ts —
// reconcile them with the real backend shape once BE-11 ships, and update the
// type guard in `femaMessage.utils.ts` at the same time.
// ---------------------------------------------------------------------------
import type {
  FemaAlertPayload,
  FemaBackendResultClass,
  FemaEnrichedAdvisory,
  FemaOutcomeFieldFinding,
} from 'types/fema.types';
import type { FemaEnvironmentChoice } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/config/femaEnvironment.config';

/**
 * The DISPLAY state the FE-04 card asks for — computed, never stored as-is (expiry in
 * particular is a client-side time comparison, not a server event; see
 * `deriveFemaThreadState`).
 */
export const FEMA_MESSAGE_STATES = ['sent', 'updated', 'cancelled', 'expired', 'failed'] as const;
export type FemaMessageDisplayState = (typeof FEMA_MESSAGE_STATES)[number];

/**
 * The ENVELOPE the backend stamps on receipt (fema.types.ts: sent/source/sender are never
 * client-supplied). The store keeps it beside the operator-authored `payload` so a reference to
 * an earlier version can carry that version's real `sender,identifier,sent` triplet.
 */
export type FemaMessageEnvelope = {
  sender: string;
  /** CAP §3.2.1 sent — when the backend stamped and (attempted to) transmit this version. */
  sent: string;
  source: string;
};

/** One version in a thread: the original Alert, or a later Update/Cancel that references it. */
export type FemaMessageRecord = {
  /** The message store's row id — stable identity for a version, independent of the CAP identifier. */
  id: string | number;
  workplaceId: number;
  /** FEMA COG that owns this stored message. */
  cogId?: string;
  global_action_id?: number | string | null;
  /**
   * The FEMA environment class of the message (`Ipaws_Message.environment`, migration 010).
   * `null` or absent: the backend wrote the row before migration 010, or it does not send the
   * column yet. The list shows no class badge for such a row.
   */
  environment?: FemaEnvironmentChoice | null;
  envelope: FemaMessageEnvelope;
  /** The CAP content as authored/stored — identifier, msgType, references, scope, info. */
  payload: FemaAlertPayload & { identifier: string };
  /** Set only once the backend has an answer; absent while a send is in flight. */
  sendResult?: {
    resultClass: FemaBackendResultClass;
    advisories: FemaEnrichedAdvisory[];
    rawResponse: string | null;
    sentAt: string | null;
  };
  /** The exact signed CAP XML the backend stored for this version — for the "what was sent" view. */
  rawCapXml: string | null;
};

/** The original Alert plus every Update/Cancel that references it, oldest first. */
export type FemaMessageThread = {
  /** The original Alert's identifier — the thread's stable key. */
  rootIdentifier: string;
  versions: FemaMessageRecord[];
};

export const isFemaMessageRecord = (value: unknown): value is FemaMessageRecord => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  const payload = candidate.payload as Record<string, unknown> | undefined;
  const envelope = candidate.envelope as Record<string, unknown> | undefined;
  return (
    (typeof candidate.id === 'string' || typeof candidate.id === 'number') &&
    !!payload &&
    typeof payload.identifier === 'string' &&
    typeof payload.msgType === 'string' &&
    !!envelope &&
    typeof envelope.sent === 'string'
  );
};

// ---------------------------------------------------------------------------
// `POST /v2/fema/:id/cancel` and `POST /v2/fema/:id/update` — mirrored field for
// field from the backend `out` schemas (`PostFemaAlertCancel.schema.ts`,
// `PostFemaAlertUpdate.schema.ts`, `capChain.types.ts`). The backend hosts the
// contract; this file never invents a field it does not send.
// ---------------------------------------------------------------------------

/**
 * What the route did about the ONE message the Cancel named (card BE-28, 2026-09-22). The
 * non-`failed` outcomes are all legitimate ends, not an error.
 */
export type FemaCancelAction =
  | 'cancelled'
  | 'refused'
  | 'already-cancelled'
  | 'skipped-expired'
  | 'skipped-never-sent'
  | 'failed';

/** The one outcome the cancel route answers with, on a 200 and on a 409 alike. */
export type FemaCancelOutcome = {
  /** TRUE ONLY when FEMA accepted the Cancel. Never inferred from the HTTP status. */
  cancelled: boolean;
  messageId: number;
  capIdentifier: string;
  action: FemaCancelAction;
  /** Why it was not cancelled — absent on the accepted path. */
  reason?: string;
  /** The id of the Cancel message written, or null when none was written. */
  cancelMessageId?: number | null;
  /** FEMA's advisory codes, verbatim. */
  codes?: string[];
};

/**
 * The answer to `POST /v2/fema/:id/cancel` — 200 when FEMA accepted, 409 when it did not or a
 * precondition refused the send. A Cancel acts on exactly the message it names: earlier and
 * later messages in that message's history stay on the air (BE-28).
 */
export type FemaCancelResponse = {
  message: string;
  result: FemaCancelOutcome;
};

/** The answer to `POST /v2/fema/:id/update`. */
export type FemaUpdateResponse = {
  message: string;
  findings?: FemaOutcomeFieldFinding[];
  result: {
    /** The new message's row id. It exists whatever FEMA answered. */
    messageId: number;
    /** The message this Update supersedes. */
    referencesMessageId: number;
    /** TRUE ONLY when FEMA accepted. Never inferred from the HTTP status. */
    accepted: boolean;
    codes: string[];
  };
};

export const isFemaCancelResponse = (value: unknown): value is FemaCancelResponse => {
  if (!value || typeof value !== 'object') return false;
  const result = (value as Record<string, unknown>).result as Record<string, unknown> | undefined;
  return !!result && typeof result.cancelled === 'boolean' && typeof result.messageId === 'number';
};

export const isFemaUpdateResponse = (value: unknown): value is FemaUpdateResponse => {
  if (!value || typeof value !== 'object') return false;
  const result = (value as Record<string, unknown>).result as Record<string, unknown> | undefined;
  return !!result && typeof result.accepted === 'boolean' && typeof result.messageId === 'number';
};
